import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "outline";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 font-condensed font-bold uppercase tracking-wide rounded-pill transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-leaf-green disabled:opacity-50 disabled:pointer-events-none";

const variants: Record<Variant, string> = {
  // DRB-66 — -ink clears AA behind white text (3.00:1 -> 4.58:1).
  primary:
    "bg-calm-orange-ink text-white hover:bg-calm-orange-ink/90 shadow-sm",
  secondary: "bg-healthy-blue text-white hover:bg-healthy-blue/90 shadow-sm",
  ghost: "bg-transparent text-night-blue hover:bg-night-blue/5",
  outline:
    "bg-white text-night-blue border-2 border-night-blue/15 hover:border-leaf-green hover:text-leaf-green-ink",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-xs",
  md: "h-11 px-6 text-sm",
  lg: "h-14 px-8 text-base",
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    to?: undefined;
    href?: undefined;
  };
// Extra attributes are forwarded on the link branches too, so a caller can set aria-label,
// data-testid, target/rel and the rest without them being silently discarded.
type LinkProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: string };
type AnchorProps = CommonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonProps | LinkProps | AnchorProps
>((props, ref) => {
  const {
    variant = "primary",
    size = "md",
    fullWidth,
    className,
    children,
    ...rest
  } = props;
  const classes = cn(
    base,
    variants[variant],
    sizes[size],
    fullWidth && "w-full",
    className,
  );

  /*
   * DRB-71 — the link branches used to render only `to`/`href` and `className`, dropping every
   * other prop on the floor. So <Button to="..." aria-label="..."> silently lost the label, and
   * <Button href="..." target="_blank" rel="noopener"> silently lost the rel, which is a real
   * security footgun rather than a cosmetic one. Only the <button> branch spread `rest`.
   *
   * Found because a data-testid never reached the DOM in a live check. Nothing was visibly
   * broken, which is exactly why it survived: a dropped prop renders fine.
   */
  if ("to" in props && props.to) {
    const { to, ...linkRest } = rest as { to?: string } & Record<
      string,
      unknown
    >;
    void to;
    return (
      <Link to={props.to} className={classes} {...linkRest}>
        {children}
      </Link>
    );
  }
  if ("href" in props && props.href) {
    const { href, ...anchorRest } = rest as { href?: string } & Record<
      string,
      unknown
    >;
    void href;
    return (
      <a href={props.href} className={classes} {...anchorRest}>
        {children}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      className={classes}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
});
Button.displayName = "Button";
